import type { ApiError } from './errors'
import { getAccessToken } from '../auth/keycloak'
import { networkError, normalizeError, timeoutError } from './errors'
import { startApiRequestSpan } from '../telemetry'

const DEFAULT_TIMEOUT_MS = 30_000

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface RequestOptions {
  signal?: AbortSignal
  timeoutMs?: number
  headers?: Record<string, string>
}

export interface BinaryResponse {
  blob: Blob
  contentType: string | null
  contentDisposition: string | null
}

function getBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL
}

function requestUrl(path: string): string {
  const base = getBaseUrl()
  if (!path.startsWith('/')) return `${base}${path}`

  const baseUrl = new URL(base, globalThis.location?.origin ?? 'http://localhost')
  if (path === baseUrl.pathname || path.startsWith(`${baseUrl.pathname}/`)) {
    return `${baseUrl.origin}${path}`
  }
  return `${base}${path}`
}

async function requestResponse(
  method: Method,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<Response> {
  const telemetrySpan = startApiRequestSpan(method, path)
  const ctrl = new AbortController()
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)

  // Chain external abort signal — if the caller cancels, we cancel.
  if (options.signal) {
    if (options.signal.aborted) {
      ctrl.abort()
    } else {
      options.signal.addEventListener('abort', () => ctrl.abort(), { once: true })
    }
  }

  try {
    const headers: Record<string, string> = { ...options.headers }
    if (telemetrySpan.traceparent && !hasHeader(headers, 'traceparent')) {
      headers.traceparent = telemetrySpan.traceparent
    }
    const token = await getAccessToken()
    if (ctrl.signal.aborted) {
      throw timeoutError()
    }
    if (token && !hasHeader(headers, 'authorization')) {
      headers.Authorization = `Bearer ${token}`
    }
    // Non-GET requests send JSON bodies — Content-Type announces that.
    if (method !== 'GET') {
      headers['Content-Type'] = 'application/json'
    }

    let res: Response
    try {
      res = await fetch(requestUrl(path), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: ctrl.signal,
      })
    } catch (cause) {
      // fetch() rejects on abort and on network failure — distinguish them.
      if (ctrl.signal.aborted) {
        throw timeoutError()
      }
      throw networkError(cause)
    }

    if (!res.ok) {
      const error = await normalizeError(res, { method, path })
      telemetrySpan.end({ status: res.status, error })
      throw error
    }

    telemetrySpan.end({ status: res.status })
    return res
  } catch (error) {
    telemetrySpan.end({ error })
    throw error
  } finally {
    clearTimeout(timer)
  }
}

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const res = await requestResponse(method, path, body, options)
  // 204 No Content - no body to parse.
  if (res.status === 204) {
    return undefined as T
  }

  return (await res.json()) as T
}

async function requestBlob(path: string, options: RequestOptions = {}): Promise<BinaryResponse> {
  const res = await requestResponse('GET', path, undefined, options)
  return {
    blob: await res.blob(),
    contentType: res.headers.get('Content-Type'),
    contentDisposition: res.headers.get('Content-Disposition'),
  }
}

function hasHeader(headers: Record<string, string>, name: string): boolean {
  return Object.keys(headers).some((headerName) => headerName.toLowerCase() === name)
}

export const api = {
  get: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('POST', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PATCH', path, body, options),
  del: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>('DELETE', path, undefined, options),
  blob: (path: string, options?: RequestOptions): Promise<BinaryResponse> =>
    requestBlob(path, options),
}

export type { ApiError }
